import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Chapter, Region, StepWithRegions, Rect, ClickType } from '@simultrain/shared';
import { walkStepChain, findChainTail, extractDisplayText, extractSpokenText, CONFIRM_TRIGGERS, CLICK_TYPES } from '@simultrain/shared';
import { useAuth } from '../auth/store.js';
import { api } from '../api/client.js';
import { RegionCanvas } from '../components/RegionCanvas.js';
import { CaptureModal } from '../components/CaptureModal.js';
import { ScreenshotPickerModal } from '../components/ScreenshotPickerModal.js';
import { ChapterSettingsModal } from '../components/ChapterSettingsModal.js';
import { ShareChapterModal } from '../components/ShareChapterModal.js';
import { useZoomWarning } from '../hooks/useZoomWarning.js';

type DrawKind = Region['kind'] | null;

const NEW_REGION: Record<NonNullable<DrawKind>, (rect: Rect, id: string) => Region> = {
  'click-correct': (rect, id) => ({
    id, kind: 'click-correct', rect, label: '', leadsTo: null, clickType: 'left', showInInstruction: true, primary: true,
  }),
  'click-error': (rect, id) => ({
    id,
    kind: 'click-error',
    rect,
    label: '',
    feedback: '',
    correctivePath: [],
    returnToStepId: null,
    clickType: 'left',
  }),
  'input': (rect, id) => ({
    id,
    kind: 'input',
    rect,
    label: '',
    expected: { kind: 'literal', value: '', caseSensitive: false, ignoreProtocol: false, ignoreTrailingSlash: false },
    placeholder: '',
    feedbackOnWrong: '',
    leadsTo: null,
    textColor: null,
    confirmTriggers: ['enter', 'tab'],
  }),
  'drag-source': (rect, id) => ({
    id,
    kind: 'drag-source',
    rect,
    label: '',
    targets: [],
  }),
  'text': (rect, id) => ({
    id,
    kind: 'text',
    rect,
    label: '',
    text: '',
    fontSize: 16,
    textColor: '#0f172a',
    backgroundColor: '#ffffff',
    borderColor: '#0f172a',
    shadow: false,
  }),
};

const PLAYER_URL = import.meta.env.VITE_PLAYER_URL ?? 'http://localhost:5174';

// Display-only mirror of the API's bump rule, so the "Publiceren als vX"
// button can show the resulting label before the publish call is made.
function bumpMinorVersion(label: string): string {
  const match = label.match(/^(.*\.)(\d+)$/);
  if (!match) return `${label}.1`;
  const [, prefix, minor] = match;
  return `${prefix}${Number(minor) + 1}`;
}

function PreviewButton({ chapterId, mode, label }: { chapterId: string; mode: string; label: string }) {
  const { accessToken } = useAuth();
  return (
    <button
      className="secondary"
      title={`Preview in Player (${mode})`}
      onClick={() => {
        const token = accessToken ? `?token=${encodeURIComponent(accessToken)}` : '';
        window.open(`${PLAYER_URL}/run/${chapterId}/${mode}${token}`, '_blank');
      }}
    >
      {label}
    </button>
  );
}

// Small dropdown used by the compact "⋯" menu and the consolidated
// screenshot control in both densities. Closes on any click inside it
// (menu items are one-shot actions) or on an outside click.
function PopoverMenu({
  trigger,
  children,
  align = 'end',
}: {
  trigger: (toggle: () => void) => React.ReactNode;
  children: React.ReactNode;
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className="popover-menu" ref={ref}>
      {trigger(() => setOpen((v) => !v))}
      {open && (
        <div className={`popover-menu-panel align-${align}`} onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

const TTS_MARKUP_RE = /\{[^{|}]*\|[^{}]*\}/;

// Only shows once the text actually contains `{weergave|uitspraak}` markup —
// no point cluttering every field with a redundant echo of itself.
function TtsPreview({ text }: { text: string }) {
  if (!TTS_MARKUP_RE.test(text)) return null;
  return (
    <div className="tts-preview">
      <div><span className="muted">Weergave:</span> {extractDisplayText(text)}</div>
      <div><span className="muted">Uitspraak:</span> {extractSpokenText(text)}</div>
    </div>
  );
}

// Build a flat ordered list: main steps in order, each followed by the corrective
// steps referenced in its click-error regions, indented.
type StepListEntry = {
  step: StepWithRegions;
  indent: number;
  // Which click-error region triggered this corrective branch (for display)
  parentRegionLabel?: string | undefined;
};

function buildStepList(steps: StepWithRegions[], startStepId: string | null): StepListEntry[] {
  const byId = new Map(steps.map((s) => [s.id, s]));
  const result: StepListEntry[] = [];
  const visited = new Set<string>();

  function appendStep(stepId: string, indent: number, parentRegionLabel?: string) {
    if (visited.has(stepId)) return;
    const step = byId.get(stepId);
    if (!step) return;
    visited.add(stepId);
    result.push({ step, indent, parentRegionLabel });

    // Append corrective steps referenced by click-error regions of this step.
    for (const region of step.regions) {
      if (region.kind !== 'click-error' || region.correctivePath.length === 0) continue;
      for (const corrId of region.correctivePath) {
        appendStep(corrId, indent + 1, region.label || 'fout-klikgebied');
      }
    }
  }

  const corrIds = new Set<string>();
  for (const step of steps) {
    for (const r of step.regions) {
      if (r.kind === 'click-error') r.correctivePath.forEach((id) => corrIds.add(id));
    }
  }
  const isMainLike = (s: StepWithRegions) =>
    s.stepKind === 'main' || (!corrIds.has(s.id) && s.stepKind !== 'corrective');

  for (const step of walkStepChain(steps, startStepId)) {
    appendStep(step.id, 0);
  }

  // Main-like steps not reached via the chain (e.g. not yet wired in) —
  // stable order by creation time so they don't jump around on reload.
  const strandedMains = steps
    .filter((s) => !visited.has(s.id) && isMainLike(s))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const s of strandedMains) {
    appendStep(s.id, 0);
  }

  // Anything still left (orphaned corrective steps not referenced by any
  // click-error) — same stable ordering.
  const remaining = steps
    .filter((s) => !visited.has(s.id))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const step of remaining) {
    result.push({ step, indent: 0 });
  }

  return result;
}

// Rewire the successNext chain so `draggedId` moves to sit immediately
// before `targetId` among main steps. Returns the new chain order plus the
// new startStepId, or null if the move isn't valid (corrective steps can't
// be dragged — their position follows the click-error region they belong to).
function reorderMainSteps(
  steps: StepWithRegions[],
  startStepId: string | null,
  draggedId: string,
  targetId: string,
): { order: StepWithRegions[]; startStepId: string } | null {
  if (draggedId === targetId) return null;
  const dragged = steps.find((s) => s.id === draggedId);
  if (!dragged || dragged.stepKind === 'corrective') return null;

  const chain = walkStepChain(steps, startStepId).filter((s) => s.stepKind !== 'corrective');
  const targetIdx = chain.findIndex((s) => s.id === targetId);
  if (targetIdx === -1) return null;

  const withoutDragged = chain.filter((s) => s.id !== draggedId);
  const insertAt = withoutDragged.findIndex((s) => s.id === targetId);
  if (insertAt === -1) return null;
  withoutDragged.splice(insertAt, 0, dragged);

  const first = withoutDragged[0];
  if (!first) return null;
  return { order: withoutDragged, startStepId: first.id };
}

export function ChapterEditorPage() {
  const { t } = useTranslation();
  const { chapterId } = useParams<{ chapterId: string }>();
  const nav = useNavigate();
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [project, setProject] = useState<{ id: string; name: string } | null>(null);
  const [siblingChapters, setSiblingChapters] = useState<Chapter[]>([]);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [steps, setSteps] = useState<StepWithRegions[]>([]);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [activeRegionId, setActiveRegionId] = useState<string | null>(null);
  const [drawing, setDrawing] = useState<DrawKind>(null);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCapture, setShowCapture] = useState(false);
  const [showScreenshotPicker, setShowScreenshotPicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [generatingTts, setGeneratingTts] = useState(false);
  const [ttsResult, setTtsResult] = useState<string | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [zoomWarningDismissed, setZoomWarningDismissed] = useState(false);
  const zoomedDifferently = useZoomWarning();
  const regionDetailRef = useRef<HTMLDivElement>(null);

  // Selecting a region (from the canvas or the list) should immediately show
  // its editable fields — without this, the panel stays scrolled to the step
  // fields above and selecting a region on the canvas looks like it did
  // nothing, since the visible effect is out of view.
  useEffect(() => {
    if (activeRegionId) regionDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeRegionId]);

  function renderScreenshotMenuItems() {
    return (
      <>
        <div className="menu-item" onClick={() => setShowCapture(true)}>📷 {t('capture.title')}</div>
        <label className="menu-item">
          📁 {t('steps.uploadScreenshot')}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadScreenshot(f);
            }}
          />
        </label>
        <div className="menu-item" onClick={() => setShowScreenshotPicker(true)}>🔁 {t('capture.reuse')}</div>
      </>
    );
  }

  const regionTools: { kind: NonNullable<DrawKind>; icon: string; label: string }[] = [
    { kind: 'click-correct', icon: '▭', label: t('regions.addClickCorrect').replace(/^\+\s*/, '') },
    { kind: 'click-error', icon: '▨', label: t('regions.addClickError').replace(/^\+\s*/, '') },
    { kind: 'input', icon: '⌨', label: t('regions.addInput').replace(/^\+\s*/, '') },
    { kind: 'drag-source', icon: '↗', label: t('regions.addDragSource').replace(/^\+\s*/, '') },
    { kind: 'text', icon: 'Aa', label: t('regions.addText').replace(/^\+\s*/, '') },
  ];

  // Re-arm the warning if the user zooms again after dismissing it once.
  useEffect(() => { if (!zoomedDifferently) setZoomWarningDismissed(false); }, [zoomedDifferently]);

  const activeStep = useMemo(() => steps.find((s) => s.id === activeStepId) ?? null, [steps, activeStepId]);
  const activeRegion = useMemo(
    () => activeStep?.regions.find((r) => r.id === activeRegionId) ?? null,
    [activeStep, activeRegionId],
  );

  const stepList = useMemo(
    () => buildStepList(steps, chapter?.startStepId ?? null),
    [steps, chapter?.startStepId],
  );

  const reload = useCallback(async () => {
    if (!chapterId) return;
    const data = await api.getChapterFull(chapterId);
    setChapter(data.chapter);
    setProject(data.project);
    setSteps(data.steps);
    setActiveStepId((prev) => prev ?? data.steps[0]?.id ?? null);
    setVersionId(data.chapter.projectVersionId);
  }, [chapterId]); // geen activeStepId dependency — anders loopt ie bij elke stap-selectie opnieuw

  // A fresh chapter (via prev/next nav or a direct link) starts from a clean
  // slate — otherwise leftover selection/modal state from the previous
  // chapter would carry over (e.g. a step id that doesn't exist here).
  useEffect(() => {
    setActiveStepId(null);
    setActiveRegionId(null);
    setDrawing(null);
    setReorderMode(false);
    setShowSettings(false);
    setShowShare(false);
    setShowCapture(false);
    setShowScreenshotPicker(false);
    setTtsResult(null);
    setError(null);
    setEditingTitle(false);
  }, [chapterId]);

  async function saveTitle() {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!chapter || !next || next === chapter.title) return;
    try {
      const updated = await api.patchChapter(chapter.id, { title: next });
      setChapter(updated);
      setSiblingChapters((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (err) {
      setError(String(err));
    }
  }

  useEffect(() => {
    if (!versionId) return;
    void api.listChapters(versionId).then(setSiblingChapters);
  }, [versionId]);

  const chapterIndex = siblingChapters.findIndex((c) => c.id === chapterId);
  const prevChapter = chapterIndex > 0 ? siblingChapters[chapterIndex - 1] : null;
  const nextChapter =
    chapterIndex >= 0 && chapterIndex < siblingChapters.length - 1 ? siblingChapters[chapterIndex + 1] : null;

  async function startDraft() {
    if (!chapter) return;
    try {
      const draft = await api.createChapterDraft(chapter.id);
      nav(`/chapters/${draft.id}`);
    } catch (err) {
      setError(String(err));
    }
  }

  // The live chapter's own visibility — separate from the draft-of-this-
  // chapter mechanism above: this hides/shows the chapter itself, still
  // subject to the parent project version's own status on top.
  async function changeChapterStatus(status: 'draft' | 'review' | 'published') {
    if (!chapter) return;
    try {
      const updated = await api.patchChapter(chapter.id, { status });
      setChapter(updated);
      setSiblingChapters((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (err) {
      setError(String(err));
    }
  }

  async function setDraftStatus(status: 'draft' | 'review') {
    if (!chapter?.liveChapter) return;
    try {
      setChapter(await api.patchChapterDraftStatus(chapter.liveChapter.id, status));
    } catch (err) {
      setError(String(err));
    }
  }

  async function publishDraft() {
    if (!chapter?.liveChapter) return;
    const nextLabel = bumpMinorVersion(chapter.liveChapter.versionLabel);
    if (!window.confirm(t('chapters.publishDraftConfirm', { version: nextLabel, title: chapter.liveChapter.title }))) return;
    try {
      const updated = await api.publishChapterDraft(chapter.liveChapter.id);
      nav(`/chapters/${updated.id}`);
    } catch (err) {
      setError(String(err));
    }
  }

  async function discardDraft() {
    if (!chapter?.liveChapter) return;
    if (!window.confirm(t('chapters.discardDraftConfirm'))) return;
    const liveId = chapter.liveChapter.id;
    try {
      await api.deleteChapterDraft(liveId);
      nav(`/chapters/${liveId}`);
    } catch (err) {
      setError(String(err));
    }
  }

  useEffect(() => {
    void reload();
  }, [reload]);

  async function generateTts() {
    if (!chapterId) return;
    setGeneratingTts(true);
    setTtsResult(null);
    setError(null);
    try {
      const res = await api.generateTts(chapterId);
      setTtsResult(t('tts.result', res));
    } catch (err) {
      setError(String(err));
    } finally {
      setGeneratingTts(false);
    }
  }

  // --- Step CRUD ---
  async function addStep() {
    if (!chapterId) return;
    const mainSteps = steps.filter((s) => s.stepKind === 'main' || s.stepKind == null);
    const created = await api.createStep(chapterId, {
      authorLabel: `Stap ${mainSteps.length + 1}`,
      instruction: '',
      taskPrompt: '',
      stageFeedback1: '',
      fallbackFeedback: '',
      stepKind: 'main',
      regions: [],
    });

    // Wire the true end of the happy-flow chain to the new step — walking
    // successNext from startStepId, not just the last array entry, since
    // array order from the API is not guaranteed.
    const tail = findChainTail(steps, chapter?.startStepId ?? null);
    let updatedSteps = [...steps, created];
    if (tail && !tail.successNext) {
      const patched = await api.patchStep(tail.id, { successNext: created.id });
      updatedSteps = updatedSteps.map((s) => (s.id === patched.id ? { ...s, ...patched } : s));
    }

    setSteps(updatedSteps);
    setActiveStepId(created.id);
    if (steps.length === 0 && chapter) {
      const updated = await api.patchChapter(chapter.id, { startStepId: created.id });
      setChapter(updated);
    }
  }

  // Drag a main step onto another to move it there — rewrites the successNext
  // chain (splice out of the old position, insert before the drop target).
  // Corrective steps aren't draggable here; their place follows the
  // click-error region they belong to.
  async function moveMainStep(dragId: string, targetId: string) {
    const move = reorderMainSteps(steps, chapter?.startStepId ?? null, dragId, targetId);
    if (!move) return;
    const { order, startStepId } = move;

    const patches: Promise<unknown>[] = [];
    for (let i = 0; i < order.length; i++) {
      const step = order[i]!;
      const wantNext = order[i + 1]?.id ?? null;
      if (step.successNext !== wantNext) {
        patches.push(api.patchStep(step.id, { successNext: wantNext }));
      }
    }
    if (chapter && startStepId !== chapter.startStepId) {
      patches.push(api.patchChapter(chapter.id, { startStepId }).then((c) => setChapter(c)));
    }
    await Promise.all(patches);
    await reload();
  }

  async function addCorrectiveStep(parentRegion: Extract<Region, { kind: 'click-error' }>) {
    if (!chapterId || !activeStep) return;
    const created = await api.createStep(chapterId, {
      authorLabel: `Zijstap (${parentRegion.label || 'fout'})`,
      instruction: '',
      taskPrompt: '',
      stageFeedback1: '',
      fallbackFeedback: '',
      stepKind: 'corrective',
      regions: [],
    });
    let updatedSteps = [...steps, created];

    // Chain onto the existing zijstap the same way addStep chains main
    // steps — walk to the current tail (the corrective step that doesn't
    // lead anywhere yet, i.e. the zijstap's exit) and wire it to the new
    // step, so repeated "+ Zijstap toevoegen" clicks build a multi-step
    // corrective flow instead of disconnected steps needing manual rewiring.
    // A brand new (empty) correctivePath has no tail, so this is a no-op
    // for the first corrective step.
    const tail = findChainTail(steps, parentRegion.correctivePath[0] ?? null);
    if (tail && !tail.successNext) {
      const patched = await api.patchStep(tail.id, { successNext: created.id });
      updatedSteps = updatedSteps.map((s) => (s.id === patched.id ? { ...s, ...patched } : s));
    }

    setSteps(updatedSteps);
    // Append to the correctivePath of this region.
    patchRegion(parentRegion.id, {
      correctivePath: [...parentRegion.correctivePath, created.id],
    } as Partial<Region>);
    setActiveStepId(created.id);
    setActiveRegionId(null);
  }

  async function deleteStep(id: string) {
    await api.deleteStep(id);
    setSteps(steps.filter((s) => s.id !== id));
    if (activeStepId === id) setActiveStepId(null);
  }

  // Mirrors `activeStep` for use inside stable callbacks (below) that must
  // NOT change identity on every keystroke — see the memo comment on
  // RegionRect for why that identity stability matters.
  const activeStepRef = useRef<StepWithRegions | null>(null);
  useEffect(() => { activeStepRef.current = activeStep; }, [activeStep]);

  // Local edits apply to `steps` state instantly; the network save is
  // debounced so that typing doesn't fire a PATCH per keystroke — each one's
  // eventual response would otherwise re-render the canvas at an
  // unpredictable moment, occasionally landing mid-drag on a region resize.
  const pendingPatchRef = useRef<Partial<StepWithRegions>>({});
  const pendingPatchStepIdRef = useRef<string | null>(null);
  const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every local edit — lets a save response detect whether it's
  // still current. Without this, typing more *during* an in-flight PATCH's
  // round trip meant that request's (now stale) response would land and
  // overwrite the newer local text with the older, already-superseded
  // version — silently reverting whatever was typed in between.
  const editVersionRef = useRef(0);

  const flushPatch = useCallback(() => {
    if (patchTimerRef.current) { clearTimeout(patchTimerRef.current); patchTimerRef.current = null; }
    const stepId = pendingPatchStepIdRef.current;
    const patch = pendingPatchRef.current;
    pendingPatchStepIdRef.current = null;
    pendingPatchRef.current = {};
    if (!stepId || Object.keys(patch).length === 0) return;
    const versionAtFlush = editVersionRef.current;
    void (async () => {
      try {
        const saved = await api.patchStep(stepId, patch);
        // A newer edit landed while this request was in flight — its own
        // (upcoming) flush is already the more current save, so applying
        // this stale response would only revert that newer text.
        if (editVersionRef.current !== versionAtFlush) return;
        setSteps((prev) => prev.map((s) => (s.id === saved.id ? { ...s, ...saved } : s)));
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 800);
      } catch (e) {
        setError(String(e));
      }
    })();
  }, []);

  // Flush (don't lose) pending edits before switching away from a step, and on unmount.
  useEffect(() => () => flushPatch(), [activeStepId, flushPatch]);

  const patchActive = useCallback((patch: Partial<StepWithRegions>) => {
    const current = activeStepRef.current;
    if (!current) return;
    const merged = { ...current, ...patch };
    activeStepRef.current = merged;
    editVersionRef.current += 1;
    setSteps((prev) => prev.map((s) => (s.id === merged.id ? merged : s)));

    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    pendingPatchStepIdRef.current = merged.id;
    if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
    patchTimerRef.current = setTimeout(flushPatch, 400);
  }, [flushPatch]);

  // --- Screenshot upload ---
  async function uploadScreenshot(file: File | Blob) {
    if (!versionId || !activeStep) return;
    const asset = await api.uploadAsset(versionId, file);
    patchActive({ screenshotAssetId: asset.id });
  }

  // Reusing a screenshot already in this version just points at its asset;
  // from another version, the file is copied into this version's own
  // storage first so it doesn't depend on a version that could later change
  // or be deleted out from under it.
  async function handleScreenshotPicked(assetId: string, sourceVersionId: string) {
    setShowScreenshotPicker(false);
    if (!versionId) return;
    if (sourceVersionId === versionId) {
      patchActive({ screenshotAssetId: assetId });
    } else {
      const copied = await api.copyAsset(versionId, assetId);
      patchActive({ screenshotAssetId: copied.id });
    }
  }

  function onPaste(e: React.ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (blob) {
          e.preventDefault();
          void uploadScreenshot(blob);
          return;
        }
      }
    }
  }

  // --- Region CRUD ---
  function createRegion(rect: Rect) {
    if (!drawing || !activeStep) return;
    const id = crypto.randomUUID();
    const region = NEW_REGION[drawing](rect, id);
    // A second (or later) click-correct region on the same step is an
    // *alternative* way to succeed, not automatically "the" one training/
    // test points at when the learner needs help — keep whichever one was
    // already primary, rather than silently ending up with two.
    if (region.kind === 'click-correct' && activeStep.regions.some((r) => r.kind === 'click-correct')) {
      region.primary = false;
    }
    patchActive({ regions: [...activeStep.regions, region] });
    setActiveRegionId(id);
    setDrawing(null);
  }

  // Stable identity (see RegionCanvas's RegionRect memo) — reads the active
  // step via ref rather than depending on it directly.
  const updateRegionRect = useCallback((id: string, rect: Rect) => {
    const current = activeStepRef.current;
    if (!current) return;
    patchActive({
      regions: current.regions.map((r) => (r.id === id ? ({ ...r, rect } as Region) : r)),
    });
  }, [patchActive]);

  function patchRegion(id: string, patch: Partial<Region>) {
    if (!activeStep) return;
    patchActive({
      regions: activeStep.regions.map((r) => (r.id === id ? ({ ...r, ...patch } as Region) : r)),
    });
  }

  function deleteRegion(id: string) {
    if (!activeStep) return;
    patchActive({
      regions: activeStep.regions.filter((r) => r.id !== id),
    });
    if (activeRegionId === id) setActiveRegionId(null);
  }

  if (!chapter) return <div className="empty">…</div>;

  return (
    <div onPaste={onPaste} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {showCapture && versionId && (
        <CaptureModal
          versionId={versionId}
          onCaptured={(assetId) => {
            void patchActive({ screenshotAssetId: assetId });
            setShowCapture(false);
          }}
          onClose={() => setShowCapture(false)}
        />
      )}
      {showScreenshotPicker && versionId && project && activeStep && (
        <ScreenshotPickerModal
          currentProjectId={project.id}
          currentProjectName={project.name}
          currentVersionId={versionId}
          currentChapterId={chapter.id}
          currentChapterTitle={chapter.title}
          excludeStepId={activeStep.id}
          onPicked={handleScreenshotPicked}
          onClose={() => setShowScreenshotPicker(false)}
        />
      )}
      {showSettings && (
        <ChapterSettingsModal
          chapter={chapter}
          onClose={() => setShowSettings(false)}
          onSaved={(updated) => setChapter(updated)}
        />
      )}
      {showShare && (
        <ShareChapterModal chapter={chapter} onClose={() => setShowShare(false)} />
      )}
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12, flexShrink: 0 }}>
        <div>
          <Link to={`/projects/${''}`} style={{ display: 'none' }} />
          <div className="row" style={{ gap: 4 }}>
            <button
              className="btn-ghost icon"
              disabled={!prevChapter}
              title={prevChapter ? `${t('chapters.previous')}: ${prevChapter.title}` : t('chapters.previous')}
              onClick={() => prevChapter && nav(`/chapters/${prevChapter.id}`)}
            >
              ◀
            </button>
            {editingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => void saveTitle()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); void saveTitle(); }
                  if (e.key === 'Escape') { e.preventDefault(); setEditingTitle(false); }
                }}
                style={{ fontSize: 20, fontWeight: 650, padding: '2px 6px', maxWidth: 420 }}
              />
            ) : (
              <h2
                style={{ margin: 0, cursor: 'pointer' }}
                title={t('chapters.editTitleHint')}
                onClick={() => { setTitleDraft(chapter.title); setEditingTitle(true); }}
              >
                {chapter.title}
              </h2>
            )}
            <button
              className="btn-ghost icon"
              disabled={!nextChapter}
              title={nextChapter ? `${t('chapters.next')}: ${nextChapter.title}` : t('chapters.next')}
              onClick={() => nextChapter && nav(`/chapters/${nextChapter.id}`)}
            >
              ▶
            </button>
            <span className="muted" style={{ fontSize: 13 }}>v{chapter.versionLabel}</span>
            {chapter.draftForChapterId == null && (
              <select
                value={chapter.status}
                title={t('chapters.statusLabel')}
                onChange={(e) => void changeChapterStatus(e.target.value as 'draft' | 'review' | 'published')}
              >
                <option value="draft">{t('chapters.statusDraft')}</option>
                <option value="review">{t('chapters.statusReview')}</option>
                <option value="published">{t('chapters.statusPublished')}</option>
              </select>
            )}
            {chapter.draftForChapterId == null && (
              chapter.draftChapterId ? (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '2px 8px' }}
                  onClick={() => nav(`/chapters/${chapter.draftChapterId}`)}
                >
                  {t('chapters.goToDraft')}
                </button>
              ) : (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '2px 8px' }}
                  onClick={() => void startDraft()}
                >
                  {t('chapters.startDraft')}
                </button>
              )
            )}
          </div>
          {/* A literally empty div has no line-box height at all, so this
              line collapsed to 0px whenever there was nothing to show —
              the resulting height jump every time "Opgeslagen" flashed on
              is what shifted the editor below it. The nbsp fallback keeps a
              stable height in every state. */}
          <div className="muted">
            {savedFlash ? t('steps.saved') : ttsResult || ' '}
          </div>
          {chapter.draftForChapterId != null && chapter.liveChapter && (
            <div className="draft-banner">
              <span>
                {t('chapters.draftBanner', { title: chapter.liveChapter.title, version: chapter.liveChapter.versionLabel })}
              </span>
              <select
                value={chapter.status === 'review' ? 'review' : 'draft'}
                onChange={(e) => void setDraftStatus(e.target.value as 'draft' | 'review')}
              >
                <option value="draft">{t('chapters.draftStatusDraft')}</option>
                <option value="review">{t('chapters.draftStatusReview')}</option>
              </select>
              <button className="secondary" onClick={() => void publishDraft()}>
                {t('chapters.publishDraft', { version: bumpMinorVersion(chapter.liveChapter.versionLabel) })}
              </button>
              <button className="btn-ghost" onClick={() => void discardDraft()}>
                {t('chapters.discardDraft')}
              </button>
            </div>
          )}
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="button-group">
            <PreviewButton chapterId={chapterId!} mode="instruction" label="▶ Instructie" />
            <PreviewButton chapterId={chapterId!} mode="training" label="Training" />
            <PreviewButton chapterId={chapterId!} mode="test" label="Toets" />
          </div>
          {chapter.draftForChapterId == null && (
            <button className="btn-ghost icon" title={t('chapters.share')} onClick={() => setShowShare(true)}>🔗</button>
          )}
          <button className="btn-ghost icon" title={t('chapters.settings')} onClick={() => setShowSettings(true)}>⚙</button>
          <button
            className="btn-ghost icon"
            title={generatingTts ? t('tts.generating') : t('tts.generate')}
            onClick={() => void generateTts()}
            disabled={generatingTts}
          >
            🔊
          </button>
          <button onClick={addStep}>+ {t('steps.create')}</button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {zoomedDifferently && !zoomWarningDismissed && (
        <div className="zoom-warning">
          {t('zoomWarning.message')}
          <button className="secondary" onClick={() => setZoomWarningDismissed(true)} title={t('zoomWarning.dismiss')}>
            {t('zoomWarning.dismiss')}
          </button>
        </div>
      )}

      <div className="editor-layout">
        {/* Left: step list */}
        <div className="panel">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>{t('steps.title')}</h3>
            <button
              className={reorderMode ? '' : 'secondary'}
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => {
                setReorderMode((v) => !v);
                setDraggedId(null);
                setDragOverId(null);
              }}
            >
              {reorderMode ? t('steps.reorderDone') : t('steps.reorder')}
            </button>
          </div>
          {reorderMode && (
            <div className="muted" style={{ fontSize: 12, margin: '8px 0' }}>
              {t('steps.reorderHint')}
            </div>
          )}
          {steps.length === 0 && <div className="muted">{t('steps.empty')}</div>}
          {stepList.map(({ step, indent, parentRegionLabel }) => {
            const draggableHere = reorderMode && indent === 0;
            return (
              <div
                key={step.id}
                className={[
                  'step-list-item',
                  step.id === activeStepId ? 'active' : '',
                  step.stepKind === 'corrective' ? 'corrective' : '',
                  draggableHere ? 'draggable' : '',
                  dragOverId === step.id ? 'drag-over' : '',
                ].filter(Boolean).join(' ')}
                style={{ paddingLeft: 8 + indent * 20 }}
                draggable={draggableHere}
                onDragStart={() => draggableHere && setDraggedId(step.id)}
                onDragOver={(e) => {
                  if (!draggableHere) return;
                  e.preventDefault();
                  setDragOverId(step.id);
                }}
                onDragLeave={() => setDragOverId((id) => (id === step.id ? null : id))}
                onDrop={(e) => {
                  if (!draggableHere || !draggedId) return;
                  e.preventDefault();
                  setDragOverId(null);
                  const dragId = draggedId;
                  setDraggedId(null);
                  void moveMainStep(dragId, step.id);
                }}
                onDragEnd={() => { setDraggedId(null); setDragOverId(null); }}
                onClick={() => {
                  if (reorderMode) return;
                  setActiveStepId(step.id);
                  setActiveRegionId(null);
                }}
              >
                {indent > 0 && (
                  <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 1 }}>
                    ↳ {parentRegionLabel}
                  </div>
                )}
                <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {draggableHere && <span className="drag-handle">⠿</span>}
                  {step.stepKind === 'corrective' ? '⤷ ' : ''}
                  {step.authorLabel || step.instruction.slice(0, 30) || '(zonder titel)'}
                  {step.autoAdvanceMs != null && (
                    <span className="muted" style={{ fontSize: 11 }}>⏱</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Center: canvas */}
        <div className="editor-center">
          {!activeStep ? (
            <div className="empty">Selecteer of maak een stap</div>
          ) : (
            <>
              <div className="docked-canvas">
                <div className="toolstrip">
                  {regionTools.map((rt) => (
                    <button
                      key={rt.kind}
                      className={`tool ${drawing === rt.kind ? 'active' : ''}`}
                      onClick={() => setDrawing(drawing === rt.kind ? null : rt.kind)}
                    >
                      {rt.icon} {rt.label}
                    </button>
                  ))}
                  <div className="toolstrip-spacer" />
                  <PopoverMenu
                    align="end"
                    trigger={(toggle) => (
                      <button className="tool" onClick={toggle}>📷 {t('editor.screenshot')} ▾</button>
                    )}
                  >
                    {renderScreenshotMenuItems()}
                  </PopoverMenu>
                </div>
                <RegionCanvas
                  imageUrl={activeStep.screenshotAssetId ? api.assetUrl(activeStep.screenshotAssetId) : null}
                  regions={activeStep.regions}
                  selectedId={activeRegionId}
                  drawingKind={drawing}
                  onSelect={setActiveRegionId}
                  onCreate={createRegion}
                  onUpdateRect={updateRegionRect}
                />
              </div>
              {drawing && <div className="muted">{t('regions.drawHint')}</div>}
              {!activeStep.screenshotAssetId && (
                <div className="muted">{t('steps.noScreenshot')}</div>
              )}
            </>
          )}
        </div>

        {/* Right: step + region details */}
        <div className="panel">
          {activeStep && (
            <>
              <h3 style={{ marginTop: 0 }}>
                {activeStep.stepKind === 'corrective' ? 'Zijstap' : 'Stap'}
              </h3>
              <div style={{ marginBottom: 8 }}>
                <label>Label (intern)</label>
                <input
                  value={activeStep.authorLabel}
                  onChange={(e) => patchActive({ authorLabel: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>{t('steps.instruction')}</label>
                <textarea
                  value={activeStep.instruction}
                  onChange={(e) => patchActive({ instruction: e.target.value })}
                />
                <TtsPreview text={activeStep.instruction} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>{t('steps.taskPrompt')}</label>
                <textarea
                  value={activeStep.taskPrompt}
                  onChange={(e) => patchActive({ taskPrompt: e.target.value })}
                />
                <TtsPreview text={activeStep.taskPrompt} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>{t('steps.stageFeedback1')} <span className="muted">(optioneel)</span></label>
                <input
                  value={activeStep.stageFeedback1 ?? ''}
                  placeholder={t('steps.stageFeedback1Placeholder')}
                  onChange={(e) => patchActive({ stageFeedback1: e.target.value })}
                />
                <TtsPreview text={activeStep.stageFeedback1 ?? ''} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>{t('steps.fallbackFeedback')}</label>
                <textarea
                  value={activeStep.fallbackFeedback ?? ''}
                  onChange={(e) => patchActive({ fallbackFeedback: e.target.value })}
                />
                <TtsPreview text={activeStep.fallbackFeedback ?? ''} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={activeStep.autoAdvanceMs != null}
                    onChange={(e) => patchActive({ autoAdvanceMs: e.target.checked ? 2000 : null })}
                  />
                  {t('steps.autoAdvance')}
                </label>
                {activeStep.autoAdvanceMs != null && (
                  <div style={{ marginTop: 6 }}>
                    <label>{t('steps.autoAdvanceMs')}</label>
                    <input
                      type="number"
                      min={0}
                      max={60000}
                      step={100}
                      value={activeStep.autoAdvanceMs}
                      onChange={(e) => patchActive({ autoAdvanceMs: Number(e.target.value) })}
                    />
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>Volgende stap (successNext)</label>
                <select
                  value={activeStep.successNext ?? ''}
                  onChange={(e) => patchActive({ successNext: e.target.value || null })}
                >
                  <option value="">
                    {activeStep.stepKind === 'corrective'
                      ? '— uitgang van de zijstap (verder met het hoofdpad) —'
                      : '— eindstap (geen vervolg) —'}
                  </option>
                  {steps
                    // A main step may only lead to another main step — jumping
                    // straight into a corrective step here would skip the
                    // click-error region that's supposed to push the recovery
                    // stack, corrupting the zijstap bookkeeping. A corrective
                    // step has no such restriction: it may lead to another
                    // corrective step (chaining a multi-step zijstap) or to
                    // any main step (also ending the zijstap, but at a
                    // specific step rather than wherever the origin's own
                    // successNext points).
                    .filter((s) => s.id !== activeStep.id && (activeStep.stepKind === 'corrective' || s.stepKind === 'main' || s.stepKind == null))
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.authorLabel || s.instruction.slice(0, 40) || s.id.slice(0, 8)}
                      </option>
                    ))}
                </select>
              </div>
              {(() => {
                // Only relevant once a step has more than one valid way to
                // succeed — with just one click-correct region there's
                // nothing to disambiguate.
                const clickCorrectRegions = activeStep.regions.filter(
                  (r): r is Extract<Region, { kind: 'click-correct' }> => r.kind === 'click-correct',
                );
                if (clickCorrectRegions.length < 2) return null;
                const primaryId = clickCorrectRegions.find((r) => r.primary !== false)?.id ?? clickCorrectRegions[0]!.id;
                return (
                  <div style={{ marginBottom: 8 }}>
                    <label>{t('steps.primaryRegion')}</label>
                    <select
                      value={primaryId}
                      onChange={(e) => {
                        const chosenId = e.target.value;
                        patchActive({
                          regions: activeStep.regions.map((r) =>
                            r.kind === 'click-correct' ? { ...r, primary: r.id === chosenId } : r,
                          ),
                        });
                      }}
                    >
                      {clickCorrectRegions.map((r, idx) => (
                        <option key={r.id} value={r.id}>{r.label || `${t('regions.title')} ${idx + 1}`}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
              <button className="danger" onClick={() => deleteStep(activeStep.id)}>
                {t('steps.delete')}
              </button>

              <hr style={{ margin: '16px 0', borderColor: 'var(--border)' }} />

              <h3>{t('regions.title')}</h3>
              {activeStep.regions.length === 0 && <div className="muted">—</div>}
              {activeStep.regions.map((r) => (
                <div
                  key={r.id}
                  className={`region-list-item ${r.id === activeRegionId ? 'active' : ''}`}
                  onClick={() => setActiveRegionId(r.id)}
                >
                  <strong>{r.kind}</strong> {r.label || ''}
                  {r.kind === 'click-error' && r.correctivePath.length > 0 && (
                    <span className="muted" style={{ marginLeft: 6 }}>
                      ({r.correctivePath.length} zijstap{r.correctivePath.length !== 1 ? 'pen' : ''})
                    </span>
                  )}
                </div>
              ))}

              {activeRegion && (
                <div ref={regionDetailRef} style={{ marginTop: 12 }}>
                  <div className="muted" style={{ marginBottom: 8 }}>{t('regions.keyboardHint')}</div>
                  <label>{t('regions.label')}</label>
                  <input
                    value={activeRegion.label}
                    onChange={(e) => patchRegion(activeRegion.id, { label: e.target.value })}
                  />
                  {(activeRegion.kind === 'click-correct' || activeRegion.kind === 'click-error') && (
                    <>
                      <label style={{ marginTop: 8 }}>{t('regions.clickType')}</label>
                      <select
                        value={activeRegion.clickType ?? 'left'}
                        onChange={(e) =>
                          patchRegion(activeRegion.id, { clickType: e.target.value as ClickType } as Partial<Region>)
                        }
                      >
                        {CLICK_TYPES.map((ct) => (
                          <option key={ct} value={ct}>{t(`regions.clickTypeOption.${ct}`)}</option>
                        ))}
                      </select>
                    </>
                  )}
                  {activeRegion.kind === 'click-correct' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={activeRegion.showInInstruction ?? true}
                        onChange={(e) =>
                          patchRegion(activeRegion.id, { showInInstruction: e.target.checked } as Partial<Region>)
                        }
                      />
                      {t('regions.showInInstruction')}
                    </label>
                  )}
                  {activeRegion.kind === 'click-error' && (
                    <>
                      <label style={{ marginTop: 8 }}>{t('regions.feedback')}</label>
                      <textarea
                        value={activeRegion.feedback}
                        onChange={(e) =>
                          patchRegion(activeRegion.id, { feedback: e.target.value } as Partial<Region>)
                        }
                      />
                      <TtsPreview text={activeRegion.feedback} />

                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>
                          {t('regions.correctivePath')}
                          {activeRegion.correctivePath.length > 0 && (
                            <span> ({activeRegion.correctivePath.length})</span>
                          )}
                        </div>
                        {activeRegion.correctivePath.length === 0 && (
                          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                            {t('regions.noCorrectivePath')}
                          </div>
                        )}
                        {activeRegion.correctivePath.map((cid, idx) => {
                          const cs = steps.find((s) => s.id === cid);
                          return (
                            <div
                              key={cid}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                marginBottom: 4,
                                fontSize: 13,
                              }}
                            >
                              <span className="muted">{idx + 1}.</span>
                              <button
                                className="secondary"
                                style={{ flex: 1, textAlign: 'left', padding: '4px 8px', fontSize: 12 }}
                                onClick={() => {
                                  setActiveStepId(cid);
                                  setActiveRegionId(null);
                                }}
                              >
                                {cs?.authorLabel || cs?.instruction.slice(0, 30) || cid.slice(0, 8)}
                              </button>
                              <button
                                className="danger"
                                style={{ padding: '4px 8px', fontSize: 12 }}
                                title="Verwijder uit zijstap-flow"
                                onClick={() =>
                                  patchRegion(activeRegion.id, {
                                    correctivePath: activeRegion.correctivePath.filter((id) => id !== cid),
                                  } as Partial<Region>)
                                }
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                        <button
                          className="secondary"
                          style={{ width: '100%', marginTop: 4 }}
                          onClick={() => void addCorrectiveStep(activeRegion)}
                        >
                          + {t('regions.addCorrectiveStep')}
                        </button>
                      </div>
                    </>
                  )}
                  {activeRegion.kind === 'input' && (
                    <>
                      <label style={{ marginTop: 8 }}>{t('regions.expectedType')}</label>
                      <select
                        value={activeRegion.expected.kind}
                        onChange={(e) =>
                          patchRegion(activeRegion.id, {
                            expected: e.target.value === 'regex'
                              ? { kind: 'regex', pattern: '', flags: 'i' }
                              : { kind: 'literal', value: '', caseSensitive: false, ignoreProtocol: false, ignoreTrailingSlash: false },
                          } as Partial<Region>)
                        }
                      >
                        <option value="literal">{t('regions.expectedTypeLiteral')}</option>
                        <option value="regex">{t('regions.expectedTypeRegex')}</option>
                      </select>
                      <label style={{ marginTop: 8 }}>{t('regions.expected')}</label>
                      {activeRegion.expected.kind === 'literal' ? (
                        <>
                          <input
                            value={activeRegion.expected.value}
                            onChange={(e) => {
                              const expected = activeRegion.expected;
                              if (expected.kind !== 'literal') return;
                              patchRegion(activeRegion.id, {
                                expected: { ...expected, value: e.target.value },
                              } as Partial<Region>);
                            }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                style={{ width: 'auto' }}
                                checked={activeRegion.expected.caseSensitive}
                                onChange={(e) => {
                                  const expected = activeRegion.expected;
                                  if (expected.kind !== 'literal') return;
                                  patchRegion(activeRegion.id, {
                                    expected: { ...expected, caseSensitive: e.target.checked },
                                  } as Partial<Region>);
                                }}
                              />
                              {t('regions.caseSensitive')}
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                style={{ width: 'auto' }}
                                checked={activeRegion.expected.ignoreProtocol}
                                onChange={(e) => {
                                  const expected = activeRegion.expected;
                                  if (expected.kind !== 'literal') return;
                                  patchRegion(activeRegion.id, {
                                    expected: { ...expected, ignoreProtocol: e.target.checked },
                                  } as Partial<Region>);
                                }}
                              />
                              {t('regions.ignoreProtocol')}
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                style={{ width: 'auto' }}
                                checked={activeRegion.expected.ignoreTrailingSlash}
                                onChange={(e) => {
                                  const expected = activeRegion.expected;
                                  if (expected.kind !== 'literal') return;
                                  patchRegion(activeRegion.id, {
                                    expected: { ...expected, ignoreTrailingSlash: e.target.checked },
                                  } as Partial<Region>);
                                }}
                              />
                              {t('regions.ignoreTrailingSlash')}
                            </label>
                          </div>
                        </>
                      ) : (
                        <>
                          <input
                            value={activeRegion.expected.pattern}
                            onChange={(e) => {
                              const expected = activeRegion.expected;
                              if (expected.kind !== 'regex') return;
                              patchRegion(activeRegion.id, {
                                expected: { ...expected, pattern: e.target.value },
                              } as Partial<Region>);
                            }}
                          />
                          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>{t('regions.regexHelp')}</p>
                        </>
                      )}
                      <label style={{ marginTop: 8 }}>{t('regions.confirmVia')}</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* Regions saved before this field existed lack it in the
                            stored JSON, so it can be undefined at runtime despite
                            the (post-parse) TS type saying otherwise. */}
                        {(() => {
                          const confirmTriggers = activeRegion.confirmTriggers ?? ['enter', 'tab'];
                          return CONFIRM_TRIGGERS.map((trigger) => (
                            <label key={trigger} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                style={{ width: 'auto' }}
                                checked={confirmTriggers.includes(trigger)}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...confirmTriggers, trigger]
                                    : confirmTriggers.filter((t) => t !== trigger);
                                  if (next.length === 0) return;
                                  patchRegion(activeRegion.id, { confirmTriggers: next } as Partial<Region>);
                                }}
                              />
                              {t(`regions.confirmTrigger.${trigger}`)}
                            </label>
                          ));
                        })()}
                      </div>
                      <label style={{ marginTop: 8 }}>{t('regions.placeholder')}</label>
                      <input
                        value={activeRegion.placeholder}
                        onChange={(e) =>
                          patchRegion(activeRegion.id, { placeholder: e.target.value } as Partial<Region>)
                        }
                      />
                      <label style={{ marginTop: 8 }}>{t('regions.feedbackOnWrong')}</label>
                      <textarea
                        value={activeRegion.feedbackOnWrong}
                        onChange={(e) =>
                          patchRegion(activeRegion.id, { feedbackOnWrong: e.target.value } as Partial<Region>)
                        }
                      />
                      <TtsPreview text={activeRegion.feedbackOnWrong} />
                      <label style={{ marginTop: 8 }}>{t('regions.textColor')}</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={activeRegion.textColor ?? '#0f172a'}
                          onChange={(e) =>
                            patchRegion(activeRegion.id, { textColor: e.target.value } as Partial<Region>)
                          }
                          style={{ width: 48, padding: 2, height: 32 }}
                        />
                        <button
                          type="button"
                          className="secondary"
                          disabled={!activeRegion.textColor}
                          onClick={() => patchRegion(activeRegion.id, { textColor: null } as Partial<Region>)}
                        >
                          {t('regions.textColorReset')}
                        </button>
                      </div>
                    </>
                  )}
                  {activeRegion.kind === 'text' && (
                    <>
                      <label style={{ marginTop: 8 }}>{t('regions.text')}</label>
                      <textarea
                        value={activeRegion.text}
                        onChange={(e) => patchRegion(activeRegion.id, { text: e.target.value } as Partial<Region>)}
                      />
                      <label style={{ marginTop: 8 }}>{t('regions.fontSize')}</label>
                      <input
                        type="number"
                        min={8}
                        max={120}
                        value={activeRegion.fontSize}
                        onChange={(e) =>
                          patchRegion(activeRegion.id, { fontSize: Number(e.target.value) } as Partial<Region>)
                        }
                      />
                      <div className="row" style={{ marginTop: 8, gap: 16 }}>
                        <div>
                          <label>{t('regions.textColorPlain')}</label>
                          <input
                            type="color"
                            value={activeRegion.textColor}
                            onChange={(e) =>
                              patchRegion(activeRegion.id, { textColor: e.target.value } as Partial<Region>)
                            }
                            style={{ width: 48, padding: 2, height: 32 }}
                          />
                        </div>
                        <div>
                          <label>{t('regions.backgroundColor')}</label>
                          <input
                            type="color"
                            value={activeRegion.backgroundColor}
                            onChange={(e) =>
                              patchRegion(activeRegion.id, { backgroundColor: e.target.value } as Partial<Region>)
                            }
                            style={{ width: 48, padding: 2, height: 32 }}
                          />
                        </div>
                        <div>
                          <label>{t('regions.borderColor')}</label>
                          <input
                            type="color"
                            value={activeRegion.borderColor}
                            onChange={(e) =>
                              patchRegion(activeRegion.id, { borderColor: e.target.value } as Partial<Region>)
                            }
                            style={{ width: 48, padding: 2, height: 32 }}
                          />
                        </div>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          style={{ width: 'auto' }}
                          checked={activeRegion.shadow}
                          onChange={(e) =>
                            patchRegion(activeRegion.id, { shadow: e.target.checked } as Partial<Region>)
                          }
                        />
                        {t('regions.shadow')}
                      </label>
                    </>
                  )}
                  <button
                    className="danger"
                    style={{ marginTop: 12 }}
                    onClick={() => deleteRegion(activeRegion.id)}
                  >
                    {t('regions.delete')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
